
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { 
    UserPermission, Unit, Owner, Vehicle, WaterReading, 
    TariffCollection, InvoiceSettings, Adjustment, ChargeRaw, 
    MonthlyStat, ActivityLog, NewsItem, FeedbackItem, Role, ResidentNotification,
    MiscRevenue, OperationalExpense, PortalPage, AdminPortalPage,
    OtherFeeConfig, AdminPage
} from './types';
import { AuthContext, NotificationContext, SettingsContext, DataRefreshContext, useAuth, useNotification, useSettings, useDataRefresh } from './contexts/AppContext';
export { useAuth, useNotification, useSettings, useDataRefresh };
import { useSmartSystemData } from '@/src/hooks/useSmartData';
import { isProduction } from './utils/env';
import Sidebar from './app-layouts/MainSidebar';
import Header from './app-layouts/MainHeader';
import Footer from './app-layouts/MainFooter';
import LoginPage from './components/pages/LoginPage';
import OverviewPage from './components/pages/OverviewPage';
import BillingPage from './components/pages/BillingPage';
import ResidentsPage from './components/pages/ResidentsPage';
import VehiclesPage from './components/pages/VehiclesPage';
import WaterPage from './components/pages/WaterPage';
import PricingPage from './components/pages/PricingPage';
import UsersPage from './components/pages/UsersPage';
import SettingsPage from './components/pages/SettingsPage';
import BackupRestorePage from './components/pages/BackupRestorePage';
import ActivityLogPage from './components/pages/ActivityLogPage';
import NewsManagementPage from './components/pages/NewsManagementPage';
import FeedbackManagementPage from './components/pages/FeedbackManagementPage';
import ValueAddedServicesPage from './components/pages/ValueAddedServicesPage';
import ExpenseManagementPage from './components/pages/ExpenseManagementPage';
import ServiceRegistrationPage from './components/pages/ServiceRegistration';
import SystemStatsPage from './components/pages/SystemStatsPage';
import ResidentLayout from './app-layouts/MainResidentLayout';
import AdminMobileLayout from './app-layouts/MainAdminMobileLayout';
import PortalHomePage from './components/pages/portal/PortalHomePage';
import PortalNewsPage from './components/pages/portal/PortalNewsPage';
import PortalBillingPage from './components/pages/portal/PortalBillingPage';
import PortalContactPage from './components/pages/portal/PortalContactPage';
import PortalProfilePage from './components/pages/portal/PortalProfilePage';
import AdminPortalHomePage from './components/pages/admin-portal/AdminPortalHomePage';
import AdminPortalResidentsPage from './components/pages/admin-portal/AdminPortalResidentsPage';
import AdminPortalVehiclesPage from './components/pages/admin-portal/AdminPortalVehiclesPage';
import AdminPortalBillingPage from './components/pages/admin-portal/AdminPortalBillingPage';
import AdminPortalVASPage from './components/pages/admin-portal/AdminPortalVASPage';
import AdminPortalExpensesPage from './components/pages/admin-portal/AdminPortalExpensesPage';
import AdminPortalNewsPage from './components/pages/admin-portal/AdminPortalNewsPage';
import AdminPortalFeedbackPage from './components/pages/admin-portal/AdminPortalFeedbackPage';
import AdminPortalSearchPage from './components/pages/admin-portal/AdminPortalSearchPage';
import AdminPortalServiceRegistrationPage from './components/pages/admin-portal/AdminPortalServiceRegistrationPage';
import BannersManagementPage from './components/pages/BannersManagementPage';
import PWAInstallPrompt from './components/ui/PWAInstallPrompt';
import Toast, { ToastMessage } from './components/ui/Toast';
import { ToastType } from './types';
import { deleteUsers, updateResidentData, importResidentsBatch, updateFeeSettings, fetchLatestLogs, updateUserProfile, saveWaterReadings, logActivity, saveTariffs, saveOtherFeeConfig, deleteOtherFeeConfig, incrementVisitCount } from './services';
import ChangePasswordModal from './components/pages/ChangePasswordModal';
import NotificationListener from './components/common/NotificationListener';
import Spinner from './components/ui/Spinner';
import { auth, db } from './firebaseConfig';
import { updatePassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const PERM_TO_PAGE: Record<string, AdminPage> = {
    overview: 'overview',
    residents: 'residents',
    vehicles: 'vehicles',
    water: 'water',
    billing: 'billing',
    newsManagement: 'newsManagement',
    feedbackManagement: 'feedbackManagement',
    serviceRegistration: 'serviceRegistration'
};

const PERM_TO_PORTAL: Record<string, AdminPortalPage> = {
    overview: 'adminPortalHome',
    residents: 'adminPortalResidents',
    vehicles: 'adminPortalVehicles',
    billing: 'adminPortalBilling'
};

const ADMIN_PAGE_TITLES: Record<AdminPage, string> = {
    overview: 'Tổng quan hệ thống', billing: 'Bảng tính phí dịch vụ', residents: 'Quản lý Cư dân & Căn hộ', vehicles: 'Quản lý Phương tiện', water: 'Quản lý Chỉ số Nước', pricing: 'Cấu hình Đơn giá', users: 'Quản lý Người dùng', settings: 'Cài đặt Hệ thống', backup: 'Sao lưu & Phục hồi', activityLog: 'Nhật ký Hoạt động', newsManagement: 'Quản lý Tin tức', feedbackManagement: 'Phản hồi Cư dân', vas: 'Dịch vụ Gia tăng (VAS)', expenses: 'Quản lý Chi phí Vận hành', serviceRegistration: 'Quản lý đăng ký dịch vụ', systemStats: 'Thống kê truy cập', banners: 'Quản lý Banner'
};

const DEFAULT_SETTINGS: InvoiceSettings = { logoUrl: '', accountName: '', accountNumber: '', bankName: '', senderEmail: '', buildingName: 'Q-Home Manager' };

const App: React.FC = () => {
    const [user, setUser] = useState<UserPermission | null>(null);
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [activePage, setActivePage] = useState<AdminPage | PortalPage | AdminPortalPage>('overview');
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
    const [unreadResidentNotifications, setUnreadResidentNotifications] = useState<ResidentNotification[]>([]);
    const [readNewsIds, setReadNewsIds] = useState<Set<string>>(new Set());
    const [resetInfo, setResetInfo] = useState<{ email: string; pass: string } | null>(null);

    const showToast = useCallback((message: string, type: ToastType, duration: number = 3000) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type, duration }]);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        const email = params.get('email');
        if (action === 'reset_default' && email) {
            const handleReset = async () => {
                try {
                    // Fetch user to get role
                    const userRef = doc(db, 'users', email);
                    const userSnap = await getDoc(userRef);
                    if (!userSnap.exists()) {
                        showToast('Không tìm thấy tài khoản này.', 'error');
                        return;
                    }
                    const userData = userSnap.data() as UserPermission;
                    const defaultPass = userData.Role === 'Resident' ? '123456a' : '123456a@';

                    // Update Firestore to default password
                    await updateUserProfile(email, { 
                        password: defaultPass, 
                        mustChangePassword: true,
                        status: 'Active' // Ensure user is active
                    });
                    setResetInfo({ email, pass: defaultPass });
                    showToast(`Mật khẩu của ${email} đã được đặt lại về mặc định (${defaultPass}). Vui lòng đăng nhập và đổi mật khẩu ngay.`, 'success', 8000);
                } catch (err) {
                    console.error("Reset Default Error:", err);
                    showToast('Không thể đặt lại mật khẩu. Vui lòng liên hệ Admin.', 'error');
                }
            };
            handleReset();
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [showToast]);

    useEffect(() => {
        const hasCounted = sessionStorage.getItem('visit_counted');
        if (!hasCounted) {
            incrementVisitCount(!!user);
            sessionStorage.setItem('visit_counted', 'true');
        }
    }, [user]);

    const { 
        units = [], owners = [], vehicles = [], waterReadings = [], charges = [], adjustments = [], users: fetchedUsers = [], news = [], feedback = [], registrations = [],
        invoiceSettings, tariffs, monthlyStats = [], lockedWaterPeriods = [], miscRevenues = [], expenses = [], otherFeeConfigs = [], refreshSystemData, hasLoaded, loading 
    } = useSmartSystemData(user);

    const [localCharges, setLocalCharges] = useState<ChargeRaw[]>([]);
    useEffect(() => { if(charges) setLocalCharges(charges); }, [charges]);

    const [localUsers, setLocalUsers] = useState<UserPermission[]>([]);
    useEffect(() => { if(fetchedUsers) setLocalUsers(fetchedUsers); }, [fetchedUsers]);

    const redirectUser = useCallback((u: UserPermission) => {
        if (u.Role === 'Resident') {
            setActivePage('portalHome');
            return;
        }
        
        const perms = u.permissions || [];
        const isAdmin = u.Role === 'Admin';
        const hasOverview = isAdmin || perms.includes('overview');
        const mobile = window.innerWidth < 768;

        if (mobile) {
            if (hasOverview) setActivePage('adminPortalHome');
            else {
                const firstAvailable = perms.find(p => PERM_TO_PORTAL[p]);
                setActivePage(firstAvailable ? PERM_TO_PORTAL[firstAvailable] : 'adminPortalMore');
            }
        } else {
            if (hasOverview) setActivePage('overview');
            else {
                const firstAvailable = perms.find(p => PERM_TO_PAGE[p]);
                setActivePage(firstAvailable ? PERM_TO_PAGE[firstAvailable] : 'users');
            }
        }
    }, []);

    useEffect(() => {
        if (!user || user.Role === 'Resident' || user.Role === 'Admin') return;
        const perms = new Set(user.permissions || []);
        if (activePage === 'overview' || activePage === 'adminPortalHome') {
            if (!perms.has('overview')) redirectUser(user);
        }
        const pageToPermMap: Record<string, string> = {
            'residents': 'residents', 'vehicles': 'vehicles', 'water': 'water', 'billing': 'billing',
            'newsManagement': 'newsManagement', 'feedbackManagement': 'feedbackManagement',
            'serviceRegistration': 'serviceRegistration',
            'vas': 'billing', 'expenses': 'billing', 'pricing': 'billing',
            'adminPortalResidents': 'residents', 'adminPortalVehicles': 'vehicles', 'adminPortalBilling': 'billing',
            'adminPortalVAS': 'billing', 'adminPortalExpenses': 'billing', 'adminPortalServiceRegistration': 'serviceRegistration'
        };
        const requiredPerm = pageToPermMap[activePage];
        if (requiredPerm && !perms.has(requiredPerm)) redirectUser(user);
    }, [activePage, user, redirectUser]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        const saved = localStorage.getItem('seen_news_ids_v2');
        if (saved) try { setReadNewsIds(new Set(JSON.parse(saved))); } catch {}
        const remembered = localStorage.getItem('rememberedUserObject');
        if (remembered) try {
            const parsed = JSON.parse(remembered);
            setUser(parsed);
            redirectUser(parsed);
        } catch { localStorage.removeItem('rememberedUserObject'); }
        return () => window.removeEventListener('resize', handleResize);
    }, [redirectUser]);

    const refreshLogs = useCallback(async () => {
        if (!user || user.Role === 'Resident') return;
        const latest = await fetchLatestLogs(50);
        setActivityLogs(latest);
    }, [user]);

    useEffect(() => { if (['overview', 'activityLog'].includes(activePage)) refreshLogs(); }, [activePage, refreshLogs]);

    const handleLogin = (loggedInUser: UserPermission, rememberMe: boolean) => {
        setUser(loggedInUser);
        if (rememberMe) localStorage.setItem('rememberedUserObject', JSON.stringify(loggedInUser));
        redirectUser(loggedInUser);
        if (loggedInUser.mustChangePassword) setIsChangePasswordModalOpen(true);
    };

    const handleLogout = () => { setUser(null); localStorage.removeItem('rememberedUserObject'); setActivePage('overview'); };

    const handleChangePassword = async (newPassword: string) => {
        if (!user) return;
        try {
            // 1. Update Firestore
            const updatedUser = { ...user, password: newPassword, mustChangePassword: false };
            await updateUserProfile(user.Email, updatedUser);
            
            // 2. Update Firebase Auth (if logged in)
            if (auth.currentUser && isProduction()) {
                await updatePassword(auth.currentUser, newPassword);
            }
            
            setUser(updatedUser);
            setIsChangePasswordModalOpen(false);
            showToast('Đổi mật khẩu thành công.', 'success');
            logActivity('UPDATE_PASSWORD', 'Auth', `Người dùng ${user.Email} đã đổi mật khẩu.`);
        } catch (err: any) {
            console.error("Change Password Error:", err);
            showToast('Lỗi khi đổi mật khẩu: ' + (err.message || 'Không xác định'), 'error');
        }
    };

    const handleUpdateUser = async (updatedUser: UserPermission, oldEmail: string) => {
        await updateUserProfile(oldEmail, updatedUser);
        if (user?.Email === oldEmail) setUser(updatedUser);
        refreshSystemData(true);
    };

    const handleDeleteUsersAction = async (emails: string[]) => {
        await deleteUsers(emails);
        refreshSystemData(true);
    };

    const handleSaveResident = async (data: any, reason: string) => {
        if (!user) return;
        await updateResidentData(units, owners, vehicles, data, { email: user.Email, role: user.Role }, reason);
        refreshSystemData(true);
        refreshLogs(); 
    };

    const handleSaveTariffs = async (newTariffs: TariffCollection, logPayload?: any) => {
        await saveTariffs(newTariffs);
        if (logPayload) await logActivity(logPayload.action, logPayload.module, logPayload.summary);
        refreshSystemData(true);
    }

    const handleSaveOtherFee = async (config: OtherFeeConfig) => {
        await saveOtherFeeConfig(config);
        refreshSystemData(true);
    };

    const handleDeleteOtherFee = async (id: string) => {
        await deleteOtherFeeConfig(id);
        refreshSystemData(true);
    };

    const handleSaveWaterReadingsProp = async (updater: React.SetStateAction<WaterReading[]>, logPayload?: any) => {
        if (typeof updater === 'function') {
            const nextReadings = updater(waterReadings);
            await saveWaterReadings(nextReadings);
        } else {
            await saveWaterReadings(updater);
        }
        if (logPayload) await logActivity(logPayload.action, logPayload.module, logPayload.summary);
        refreshSystemData(true);
    };

    const handleMarkNewsAsRead = useCallback((newsId: string) => {
        setReadNewsIds(prev => {
            const next = new Set(prev); next.add(newsId);
            localStorage.setItem('seen_news_ids_v2', JSON.stringify(Array.from(next)));
            return next;
        });
    }, []);

    const notifications = useMemo(() => ({ 
        unreadNews: news.filter((n:any) => !n.isArchived && !readNewsIds.has(n.id)).length,
        hasUnpaidBill: localCharges.some((c:any) => c.UnitID === user?.residentId && !['paid', 'paid_tm', 'paid_ck'].includes(c.paymentStatus)),
        unreadList: unreadResidentNotifications
    }), [news, localCharges, user, unreadResidentNotifications, readNewsIds]);

    // Handle App Badge and Notification Permissions
    useEffect(() => {
        if (!user) {
            if ('clearAppBadge' in navigator) navigator.clearAppBadge();
            return;
        }

        // 1. Request Notification Permissions
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    console.log('Notification permission granted.');
                }
            });
        }

        // 2. Update App Badge based on unread notifications
        if ('setAppBadge' in navigator) {
            const totalUnread = notifications.unreadNews + (notifications.unreadList?.length || 0);
            if (totalUnread > 0) {
                navigator.setAppBadge(totalUnread).catch(err => console.error('Error setting badge:', err));
            } else {
                navigator.clearAppBadge().catch(err => console.error('Error clearing badge:', err));
            }
        }
    }, [user, notifications.unreadNews, notifications.unreadList]);

    const renderAdminPage = () => {
        const commonProps = { allUnits: units, allOwners: owners, allVehicles: vehicles, allWaterReadings: waterReadings, charges: localCharges, activityLogs, news, monthlyStats, lockedWaterPeriods, invoiceSettings: invoiceSettings || DEFAULT_SETTINGS, tariffs, role: user!.Role, permissions: user!.permissions || [], refreshData: () => refreshSystemData(true) };
        switch (activePage as AdminPage) {
            case 'overview': return <OverviewPage {...commonProps} feedback={[]} onNavigate={(p) => setActivePage(p as AdminPage)} />;
            case 'billing': return <BillingPage charges={localCharges} setCharges={setLocalCharges} allData={{ units, owners, vehicles, waterReadings, tariffs, adjustments, activityLogs, monthlyStats, lockedWaterPeriods, otherFeeConfigs }} onUpdateAdjustments={() => {}} role={user!.Role} invoiceSettings={invoiceSettings || DEFAULT_SETTINGS} onRefresh={() => refreshSystemData(true)} />;
            case 'residents': return <ResidentsPage units={units} owners={owners} vehicles={vehicles} activityLogs={activityLogs} onSaveResident={handleSaveResident} onImportData={importResidentsBatch} onDeleteResidents={()=>{}} role={user!.Role} currentUser={user!} onNavigate={(p) => setActivePage(p as AdminPage)} />;
            case 'vehicles': return <VehiclesPage vehicles={vehicles} units={units} owners={owners} activityLogs={activityLogs} onSetVehicles={()=>{}} role={user!.Role} />;
            case 'water': return <WaterPage waterReadings={waterReadings} setWaterReadings={handleSaveWaterReadingsProp} allUnits={units} role={user!.Role} tariffs={tariffs} lockedPeriods={lockedWaterPeriods} refreshData={refreshSystemData} activityLogs={activityLogs} />;
            case 'pricing': return <PricingPage tariffs={tariffs} setTariffs={handleSaveTariffs} role={user!.Role} otherFeeConfigs={otherFeeConfigs} units={units} onSaveOtherFee={handleSaveOtherFee} onDeleteOtherFee={handleDeleteOtherFee} />;
            case 'users': return <UsersPage users={localUsers} setUsers={setLocalUsers} units={units} role={user!.Role} loading={loading} />;
            case 'settings': return <SettingsPage invoiceSettings={invoiceSettings || DEFAULT_SETTINGS} setInvoiceSettings={(s) => updateFeeSettings(s)} role={user!.Role} />;
            case 'activityLog': return <ActivityLogPage logs={activityLogs} onUndo={()=>{}} role={user!.Role} />;
            case 'newsManagement': return <NewsManagementPage news={news} setNews={()=>{}} role={user!.Role} users={fetchedUsers} />;
            case 'feedbackManagement': return <FeedbackManagementPage role={user!.Role} units={units} owners={owners} />;
            case 'vas': return <ValueAddedServicesPage />;
            case 'expenses': return <ExpenseManagementPage />;
            case 'serviceRegistration': return <ServiceRegistrationPage role={user!.Role} />;
            case 'systemStats': return <SystemStatsPage />;
            case 'banners': return <BannersManagementPage />;
            case 'backup': return <BackupRestorePage allData={{ units, owners, vehicles, waterReadings, charges, adjustments, users: localUsers, tariffs, otherFeeConfigs }} onRestore={(d) => refreshSystemData(true)} role={user!.Role} />;
            default: return <OverviewPage {...commonProps} feedback={[]} onNavigate={(p) => setActivePage(p as AdminPage)} />;
        }
    };

    const renderResidentPage = () => {
        if (!hasLoaded) return <Spinner />;
        const unit = units.find((u:any) => u.UnitID === user!.residentId) || null;
        const owner = owners.find((o:any) => o.OwnerID === unit?.OwnerID) || null;
        const props = { user: user!, owner, charges: localCharges, news, setActivePage: setActivePage as any, readIds: readNewsIds, onReadNews: handleMarkNewsAsRead };
        switch (activePage as PortalPage) {
            case 'portalHome': return <PortalHomePage {...props} />;
            case 'portalNews': return <PortalNewsPage {...props} />;
            case 'portalBilling': return <PortalBillingPage charges={localCharges} user={user!} />;
            case 'portalContact': return <PortalContactPage hotline={invoiceSettings?.HOTLINE || '0834.88.66.86'} onSubmitFeedback={()=>{}} owner={owner} unit={unit} />;
            case 'portalProfile': return <PortalProfilePage user={user!} owner={owner!} onUpdateOwner={()=>{}} onChangePassword={() => setIsChangePasswordModalOpen(true)} />;
            default: return <PortalHomePage {...props} />;
        }
    };

    const renderAdminMobilePage = () => {
        const props = { units, vehicles, charges: localCharges, monthlyStats, news, owners, miscRevenues, expenses, registrations, role: user!.Role, permissions: user!.permissions || [] };
        const isAdmin = user!.Role === 'Admin';
        const userPerms = new Set(user!.permissions || []);
        const hasPerm = (perm: string) => isAdmin || userPerms.has(perm);
        switch (activePage as AdminPortalPage) {
            case 'adminPortalHome': return <AdminPortalHomePage {...props} onNavigate={(p) => setActivePage(p as AdminPortalPage)} />;
            case 'adminPortalBilling': return <AdminPortalBillingPage charges={localCharges} units={units} owners={owners} />;
            case 'adminPortalResidents': return <AdminPortalResidentsPage units={units} owners={owners} vehicles={vehicles} />;
            case 'adminPortalVehicles': return <AdminPortalVehiclesPage vehicles={vehicles} units={units} owners={owners} />;
            case 'adminPortalVAS': return <AdminPortalVASPage miscRevenues={miscRevenues} />;
            case 'adminPortalExpenses': return <AdminPortalExpensesPage expenses={expenses} />;
            case 'adminPortalNews': return <AdminPortalNewsPage news={news} refreshData={() => refreshSystemData(true)} />;
            case 'adminPortalFeedback': return <AdminPortalFeedbackPage feedback={feedback} units={units} owners={owners} refreshData={() => refreshSystemData(true)} />;
            case 'adminPortalSearch': return <AdminPortalSearchPage units={units} owners={owners} vehicles={vehicles} charges={localCharges} activityLogs={activityLogs} setActivePage={setActivePage} />;
            case 'adminPortalServiceRegistration': return <AdminPortalServiceRegistrationPage units={units} owners={owners} />;
            case 'adminPortalMore': return (
                <div className="p-4 space-y-4">
                    {hasPerm('newsManagement') && <button onClick={() => setActivePage('adminPortalNews')} className="w-full p-4 bg-white rounded-xl shadow-sm border flex justify-between items-center font-bold text-gray-800">Quản lý Tin tức <span>→</span></button>}
                    {hasPerm('feedbackManagement') && <button onClick={() => setActivePage('adminPortalFeedback')} className="w-full p-4 bg-white rounded-xl shadow-sm border flex justify-between items-center font-bold text-gray-800">Phản hồi Cư dân <span>→</span></button>}
                    {hasPerm('serviceRegistration') && <button onClick={() => setActivePage('adminPortalServiceRegistration')} className="w-full p-4 bg-white rounded-xl shadow-sm border flex justify-between items-center font-bold text-gray-800">Đăng ký Dịch vụ <span>→</span></button>}
                    <button onClick={() => handleLogout()} className="w-full p-4 bg-red-50 text-red-600 rounded-xl shadow-sm border flex justify-between items-center font-black">Đăng xuất <span>⏻</span></button>
                </div>
            );
            default: return <AdminPortalHomePage {...props} onNavigate={(p) => setActivePage(p as AdminPortalPage)} />;
        }
    };

    return (
        <AuthContext.Provider value={{ user, login: handleLogin, logout: handleLogout, updateUser: handleUpdateUser, handleDeleteUsers: handleDeleteUsersAction }}>
            <NotificationContext.Provider value={{ showToast }}>
                <SettingsContext.Provider value={{ invoiceSettings: invoiceSettings || DEFAULT_SETTINGS, setInvoiceSettings: async (s) => { await updateFeeSettings(s); refreshSystemData(true); } }}>
                    <DataRefreshContext.Provider value={{ refreshData: refreshSystemData }}>
                        {!user ? <LoginPage users={localUsers} onLogin={handleLogin} allOwners={owners} allUnits={units} resetInfo={resetInfo} /> : (
                            <>
                                <NotificationListener userId={user.Username || user.Email} onUpdateList={setUnreadResidentNotifications} />
                                {user.Role === 'Resident' ? (
                                    <ResidentLayout activePage={activePage as PortalPage} setActivePage={setActivePage as any} user={user} owner={owners.find((o:any) => o.OwnerID === units.find((u:any) => u.UnitID === user.residentId)?.OwnerID) || null} onUpdateOwner={()=>{}} onChangePassword={() => setIsChangePasswordModalOpen(true)} notifications={notifications}>
                                        {renderResidentPage()}
                                    </ResidentLayout>
                                ) : isMobile ? (
                                    <AdminMobileLayout activePage={activePage as AdminPortalPage} setActivePage={setActivePage as any} user={user}>
                                        {renderAdminMobilePage()}
                                    </AdminMobileLayout>
                                ) : (
                                    <div className="flex h-screen bg-gray-50 overflow-hidden">
                                        <Sidebar activePage={activePage as AdminPage} setActivePage={setActivePage as any} role={user.Role} loading={loading} onRefresh={() => refreshSystemData(true)} />
                                        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                                            <Header pageTitle={ADMIN_PAGE_TITLES[activePage as AdminPage] || 'Quản lý'} onNavigate={setActivePage as any} />
                                            <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">{!hasLoaded ? <Spinner /> : renderAdminPage()}</main>
                                            <Footer />
                                        </div>
                                    </div>
                                )}
                                <Toast toasts={toasts} onClose={(id) => setToasts(t => t.filter(x => x.id !== id))} onClearAll={() => setToasts([])} />
                                {isChangePasswordModalOpen && <ChangePasswordModal onClose={() => setIsChangePasswordModalOpen(false)} onSave={handleChangePassword} />}
                                <PWAInstallPrompt />
                            </>
                        )}
                    </DataRefreshContext.Provider>
                </SettingsContext.Provider>
            </NotificationContext.Provider>
        </AuthContext.Provider>
    );
};

export default App;
